import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Inbox, PhoneCall } from "lucide-react";
import { adminApi } from "../../lib/adminApi";
import { money } from "../../lib/api";
import { Card, PageTitle, fmtDateTime } from "../../components/admin/AdminUI";
import { StatusBadge } from "../../components/track/StatusBadge";

export const ShipmentRows = ({ items }) => {
  const navigate = useNavigate();
  if (!items.length) return <p className="text-sm text-slate-500 py-6 text-center" data-testid="shipments-empty">No shipments found.</p>;
  return (
    <div className="overflow-x-auto -mx-5">
      <table className="w-full text-sm min-w-[760px]" data-testid="shipments-table">
        <thead>
          <tr className="text-left font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200">
            {["Tracking #", "Status", "Customer", "Item", "Route", "Price", "Created"].map((h) => <th key={h} className="px-5 py-3 font-medium">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.tracking_number} onClick={() => navigate(`/admin/shipments/${s.tracking_number}`)} className="border-b border-slate-100 hover:bg-amber-50/50 cursor-pointer" data-testid={`shipment-row-${s.tracking_number}`}>
              <td className="px-5 py-3 font-mono font-bold text-slate-900">{s.tracking_number}{s.is_demo && <span className="ml-2 text-[10px] text-slate-400">DEMO</span>}</td>
              <td className="px-5 py-3"><StatusBadge status={s.status} testid={`row-status-${s.tracking_number}`} /></td>
              <td className="px-5 py-3"><div className="text-slate-900">{s.customer.full_name}</div><div className="text-xs text-slate-500">{s.customer.email}</div></td>
              <td className="px-5 py-3 text-slate-700">{s.item_summary}</td>
              <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{s.origin} → {s.destination}</td>
              <td className="px-5 py-3 whitespace-nowrap"><div className="text-slate-900">{s.final_price != null ? money(s.final_price) : "—"}</div><div className="text-xs text-slate-500">est. {money(s.estimate)}</div></td>
              <td className="px-5 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(s.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { adminApi.get("/admin/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <p className="text-slate-500">Loading…</p>;
  return (
    <div data-testid="admin-dashboard">
      <PageTitle eyebrow="Overview" title="Dashboard" />
      <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-8">
        <div className="panel p-5 bg-brand-600 !border-brand-600"><div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-100">Total shipments</div><div className="mt-2 font-display font-black text-4xl text-white" data-testid="dash-total">{d.total_shipments}</div></div>
        <Link to="/admin/messages" className="panel p-5 lift" data-testid="dash-unread-link">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500"><Inbox className="w-3.5 h-3.5" />Unread messages</div>
          <div className="mt-2 font-display font-black text-4xl text-brand-600" data-testid="dash-unread">{d.unread_messages}</div>
        </Link>
        <Link to="/admin/leads" className="panel p-5 lift" data-testid="dash-leads-link">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500"><PhoneCall className="w-3.5 h-3.5" />New chat leads</div>
          <div className="mt-2 font-display font-black text-4xl text-brand-600" data-testid="dash-new-leads">{d.new_leads}</div>
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-3 mb-10" data-testid="dash-status-counts">
        {Object.entries(d.status_counts).map(([s, n]) => (
          <Link key={s} to={`/admin/shipments?status=${encodeURIComponent(s)}`} className="panel p-4 lift" data-testid={`dash-count-${s.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="text-xs text-slate-600 leading-tight min-h-[2rem]">{s}</div>
            <div className="mt-1 font-display font-black text-3xl text-slate-900">{n}</div>
          </Link>
        ))}
      </div>
      <Card title="Recent shipments" actions={<Link to="/admin/shipments" className="btn-ghost h-9 px-3 text-xs" data-testid="dash-all-shipments">View all</Link>}>
        <ShipmentRows items={d.recent_shipments} />
      </Card>
    </div>
  );
}
